import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, CheckCircle2, XCircle, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { RefreshTokenAssistant } from './RefreshTokenAssistant';

type ApiProvider = 'amazon_sp' | 'amazon_ads' | 'seller_wallet';

interface CredentialsFormProps {
  clientId: string;
  provider: ApiProvider;
  onSaveSuccess?: () => void;
}

const amazonSpSchema = z.object({
  solution_id: z.string().trim().min(1, 'Solution ID requis'),
  client_id: z.string().trim().min(1, 'Client ID requis'),
  client_secret: z.string().trim().min(1, 'Client Secret requis'),
  refresh_token: z.string().trim().min(1, 'Refresh Token requis'),
  aws_access_key_id: z.string().trim().min(1, 'AWS Access Key ID requis'),
  aws_secret_access_key: z.string().trim().min(1, 'AWS Secret Access Key requis'),
});

const amazonAdsSchema = z.object({
  client_id: z.string().trim().min(1, 'Client ID requis'),
  client_secret: z.string().trim().min(1, 'Client Secret requis'),
  refresh_token: z.string().trim().min(1, 'Refresh Token requis'),
  profile_id: z.string().trim().optional(),
});

const sellerWalletSchema = z.object({
  client_id: z.string().trim().min(1, 'Client ID requis'),
  client_secret: z.string().trim().min(1, 'Client Secret requis'),
  refresh_token: z.string().trim().min(1, 'Refresh Token requis'),
});

const getSchema = (provider: ApiProvider) => {
  switch (provider) {
    case 'amazon_sp':
      return amazonSpSchema;
    case 'amazon_ads':
      return amazonAdsSchema;
    case 'seller_wallet':
      return sellerWalletSchema;
  }
};

const getProviderLabel = (provider: ApiProvider) => {
  switch (provider) {
    case 'amazon_sp':
      return 'Amazon SP-API (Selling Partner API)';
    case 'amazon_ads':
      return 'Amazon Advertising API';
    case 'seller_wallet':
      return 'Amazon Seller Wallet API';
  }
};

export function CredentialsForm({ clientId, provider, onSaveSuccess }: CredentialsFormProps) {
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string; errorCode?: string } | null>(null);
  const [showSecrets, setShowSecrets] = useState(false);
  const [existingCreds, setExistingCreds] = useState<any>(null);
  const [showTokenAssistant, setShowTokenAssistant] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const schema = getSchema(provider);
  const { register, handleSubmit, formState: { errors }, reset, watch } = useForm({
    resolver: zodResolver(schema),
  });
  
  const watchedClientId = watch('client_id');
  const watchedClientSecret = watch('client_secret');

  useEffect(() => {
    loadExistingCredentials();
  }, [clientId, provider]);

  const loadExistingCredentials = async () => {
    try {
      const { data, error } = await supabase
        .from('api_credentials')
        .select('*')
        .eq('client_id', clientId)
        .eq('provider', provider)
        .maybeSingle();

      if (data && !error) {
        setExistingCreds(data);
        // Ne pas afficher les secrets existants pour la sécurité
        const secrets = (data.encrypted_secrets as Record<string, any>) || {};
        reset({
          ...secrets,
          client_secret: '••••••••••••',
          refresh_token: '••••••••••••',
          aws_secret_access_key: secrets.aws_secret_access_key ? '••••••••••••' : '',
        });
        const hasToken = !!secrets.refresh_token;
        setEditMode(!hasToken);
      } else {
        setEditMode(true);
      }
    } catch (err) {
      console.error('Error loading credentials:', err);
    }
  };

  const onSubmit = async (data: any) => {
    setLoading(true);
    setTestResult(null);

    try {
      // Appel à l'edge function pour stocker les credentials de manière sécurisée
      const { data: result, error } = await supabase.functions.invoke('update-credentials', {
        body: {
          client_id: clientId,
          provider,
          credentials: data,
        },
      });

      if (error) throw error;

      toast.success('Credentials sauvegardées avec succès');
      onSaveSuccess?.();
      await loadExistingCredentials();
    } catch (error: any) {
      console.error('Error saving credentials:', error);
      toast.error(error.message || 'Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  const testConnection = async () => {
    if (!existingCreds) {
      toast.error('Veuillez d\'abord sauvegarder vos credentials');
      return;
    }

    setTesting(true);
    setTestResult(null);

    try {
      const { data: result, error } = await supabase.functions.invoke('test-credentials', {
        body: {
          client_id: clientId,
          provider,
        },
      });

      if (error) throw error;

      setTestResult({
        success: result.success,
        message: result.message || (result.success ? 'Connexion réussie !' : 'Échec de la connexion'),
        errorCode: result.errorCode
      });

      // Show token assistant for auth-related errors
      if (!result.success && (result.errorCode === 'invalid_grant' || result.errorCode === 'invalid_client')) {
        setShowTokenAssistant(true);
      }

      if (result.success) {
        toast.success('Test de connexion réussi');
      } else {
        toast.error('Test de connexion échoué');
      }
    } catch (error: any) {
      console.error('Error testing connection:', error);
      setTestResult({
        success: false,
        message: error.message || 'Erreur lors du test',
      });
      toast.error('Erreur lors du test de connexion');
    } finally {
      setTesting(false);
    }
  };

  const renderFields = () => {
    switch (provider) {
      case 'amazon_sp':
        return (
          <>
            <div className="space-y-2">
              <Label htmlFor="solution_id">Solution ID</Label>
              <Input
                id="solution_id"
                {...register('solution_id')}
                placeholder="amzn1.sp.solution.xxxxx"
                className="bg-[#0a0a0a] border-[#2a2a2a]"
              />
              {errors.solution_id && (
                <p className="text-sm text-red-500">{errors.solution_id.message as string}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="client_id">Client ID</Label>
              <Input
                id="client_id"
                {...register('client_id')}
                placeholder="amzn1.application-oa2-client.xxxxx"
                className="bg-[#0a0a0a] border-[#2a2a2a]"
              />
              {errors.client_id && (
                <p className="text-sm text-red-500">{errors.client_id.message as string}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="aws_access_key_id">AWS Access Key ID</Label>
              <Input
                id="aws_access_key_id"
                {...register('aws_access_key_id')}
                placeholder="AKIA..."
                className="bg-[#0a0a0a] border-[#2a2a2a]"
              />
              {errors.aws_access_key_id && (
                <p className="text-sm text-red-500">{errors.aws_access_key_id.message as string}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="aws_secret_access_key">AWS Secret Access Key</Label>
              <Input
                id="aws_secret_access_key"
                type={showSecrets ? 'text' : 'password'}
                {...register('aws_secret_access_key')}
                placeholder="AWS Secret Key"
                className="bg-[#0a0a0a] border-[#2a2a2a]"
              />
              {errors.aws_secret_access_key && (
                <p className="text-sm text-red-500">{errors.aws_secret_access_key.message as string}</p>
              )}
            </div>
          </>
        );

      case 'amazon_ads':
        return (
          <>
            <div className="space-y-2">
              <Label htmlFor="client_id">Client ID</Label>
              <Input
                id="client_id"
                {...register('client_id')}
                placeholder="amzn1.application-oa2-client.xxxxx"
                className="bg-[#0a0a0a] border-[#2a2a2a]"
              />
              {errors.client_id && (
                <p className="text-sm text-red-500">{errors.client_id.message as string}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="profile_id">Profile ID (optionnel)</Label>
              <Input
                id="profile_id"
                {...register('profile_id')}
                placeholder="Votre Profile ID"
                className="bg-[#0a0a0a] border-[#2a2a2a]"
              />
            </div>
          </>
        );

      case 'seller_wallet':
        return (
          <div className="space-y-2">
            <Label htmlFor="client_id">Client ID</Label>
            <Input
              id="client_id"
              {...register('client_id')}
              placeholder="amzn1.application-oa2-client.xxxxx"
              className="bg-[#0a0a0a] border-[#2a2a2a]"
            />
            {errors.client_id && (
              <p className="text-sm text-red-500">{errors.client_id.message as string}</p>
            )}
          </div>
        );
    }
  };

  return (
    <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg p-6">
      <h3 className="text-lg font-semibold text-[#E6EAED] mb-4">
        {getProviderLabel(provider)}
      </h3>

      {(!existingCreds || editMode) ? (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {renderFields()}

          {/* Client Secret - commun à tous */}
          <div className="space-y-2">
            <Label htmlFor="client_secret">Client Secret</Label>
            <div className="relative">
              <Input
                id="client_secret"
                type={showSecrets ? 'text' : 'password'}
                {...register('client_secret')}
                placeholder="amzn1.oa2-cs.v1.xxxxx"
                className="bg-[#0a0a0a] border-[#2a2a2a] pr-10"
              />
              <button
                type="button"
                onClick={() => setShowSecrets(!showSecrets)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-[#E6EAED]/50 hover:text-[#E6EAED]"
              >
                {showSecrets ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {errors.client_secret && (
              <p className="text-sm text-red-500">{errors.client_secret.message as string}</p>
            )}
          </div>

          {/* Refresh Token - commun à tous */}
          <div className="space-y-2">
            <Label htmlFor="refresh_token">Refresh Token</Label>
            <Input
              id="refresh_token"
              type={showSecrets ? 'text' : 'password'}
              {...register('refresh_token')}
              placeholder="Atzr|xxxxx"
              className="bg-[#0a0a0a] border-[#2a2a2a]"
            />
            {errors.refresh_token && (
              <p className="text-sm text-red-500">{errors.refresh_token.message as string}</p>
            )}
          </div>

          {/* Test result */}
          {testResult && (
            <div className={`p-3 rounded-lg space-y-2 ${
              testResult.success 
                ? 'bg-green-500/10 border border-green-500/50' 
                : 'bg-red-500/10 border border-red-500/50'
            }`}>
              <div className="flex items-start gap-2">
                {testResult.success ? (
                  <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5" />
                ) : (
                  <XCircle className="w-5 h-5 text-red-500 mt-0.5" />
                )}
                <div className="flex-1">
                  <p className={`font-medium ${testResult.success ? 'text-green-400' : 'text-red-400'}`}>
                    {testResult.message}
                  </p>
                  {testResult.errorCode && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Code erreur: {testResult.errorCode}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Refresh Token Assistant - Show when client_id and client_secret are filled */}
          {provider === 'amazon_sp' && watchedClientId && watchedClientSecret && 
           watchedClientSecret !== '••••••••••••' && (
            <RefreshTokenAssistant
              clientId={watchedClientId}
              clientSecret={watchedClientSecret}
              onTokenGenerated={(refreshToken) => {
                reset({ 
                  ...(existingCreds?.encrypted_secrets || {}),
                  solution_id: watch('solution_id'),
                  client_id: watchedClientId,
                  client_secret: watchedClientSecret,
                  aws_access_key_id: watch('aws_access_key_id'),
                  aws_secret_access_key: watch('aws_secret_access_key'),
                  refresh_token: refreshToken 
                });
                setShowTokenAssistant(false);
                toast.success('Refresh Token ajouté, veuillez sauvegarder');
              }}
            />
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <Button
              type="submit"
              disabled={loading}
              className="bg-[#FF8A00] hover:bg-[#FF8A00]/90"
            >
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Sauvegarder
            </Button>

            {existingCreds && (
              <Button
                type="button"
                variant="outline"
                onClick={testConnection}
                disabled={testing}
              >
                {testing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Tester la connexion
              </Button>
            )}

            {existingCreds && (
              <>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setEditMode(false)}
                >
                  Annuler
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  onClick={async () => {
                    if (confirm('Êtes-vous sûr de vouloir réinitialiser ces credentials ?')) {
                      reset({});
                      setExistingCreds(null);
                      setEditMode(true);
                      toast.info('Credentials réinitialisées');
                    }
                  }}
                >
                  Réinitialiser
                </Button>
              </>
            )}
          </div>
        </form>
      ) : (
        <div className="space-y-4">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-green-500/20 text-green-400 border border-green-500/50">Connecté</span>
                {existingCreds?.updated_at && (
                  <span className="text-xs text-[#E6EAED]/50">
                    Mis à jour: {new Date(existingCreds.updated_at).toLocaleString()}
                  </span>
                )}
              </div>
              <p className="text-[#E6EAED]/70 text-sm">
                Credentials enregistrés. Vous n'aurez plus à les saisir.
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={testConnection}
                disabled={testing}
              >
                {testing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Tester la connexion
              </Button>
              <Button
                type="button"
                onClick={() => setEditMode(true)}
                className="bg-[#FF8A00] hover:bg-[#FF8A00]/90"
              >
                Modifier
              </Button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
